import React, { useEffect, useState } from "react";

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/omega/v2`;

// Auth is cookie-based; these fetch calls were missing credentials:"include"
// entirely, so no auth cookie was ever sent cross-origin (frontend and
// backend are different origins in production) — every call here 401'd,
// including the kill switch. CSRF token: login/auth-me responses hand it
// back in the JSON body, stashed in localStorage under this key by App.js.
function csrfHeaders() {
  try {
    const stored = localStorage.getItem("roi_csrf_token");
    if (stored) return { "X-CSRF-Token": stored };
  } catch {}
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
}

const STATE_COLORS = {
  OBSERVE:  { bg: "rgba(56,189,248,0.12)",  color: "#38bdf8",  border: "rgba(56,189,248,0.3)"  },
  ASSISTED: { bg: "rgba(167,139,250,0.12)", color: "#a78bfa",  border: "rgba(167,139,250,0.3)" },
  ARMED:    { bg: "rgba(251,191,36,0.12)",  color: "#fbbf24",  border: "rgba(251,191,36,0.3)"  },
  LIVE:     { bg: "rgba(74,222,128,0.12)",  color: "#4ade80",  border: "rgba(74,222,128,0.3)"  },
  DEFENSE:  { bg: "rgba(248,113,113,0.12)", color: "#f87171",  border: "rgba(248,113,113,0.3)" },
  LOCKED:   { bg: "rgba(248,113,113,0.2)",  color: "#ef4444",  border: "rgba(239,68,68,0.5)"   },
};

const STATES = ["OBSERVE", "ASSISTED", "ARMED", "LIVE", "DEFENSE", "LOCKED"];

export default function OCIControlCenter() {
  const [status, setStatus] = useState({});
  const [execution, setExecution] = useState({});
  const [settingState, setSettingState] = useState(false);
  const [killing, setKilling] = useState(false);
  const [killMsg, setKillMsg] = useState("");

  useEffect(() => {
    const tick = async () => {
      try {
        const [s, e] = await Promise.all([
          fetch(`${API_V2}/oci/status`, { credentials: "include", headers: csrfHeaders() }).then(r => r.json()),
          fetch(`${API_V2}/execution/status`, { credentials: "include", headers: csrfHeaders() }).then(r => r.json()),
        ]);
        setStatus(s);
        setExecution(e);
      } catch {}
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, []);

  const setState = async (s) => {
    setSettingState(true);
    try {
      const res = await fetch(`${API_V2}/oci/state`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({ state: s }),
      }).then(r => r.json());
      setStatus(res);
    } catch {} finally { setSettingState(false); }
  };

  const killSwitch = async () => {
    setKilling(true);
    try {
      await fetch(`${API_V2}/execution/kill`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({ reason: "Manual kill from OCI Control Center" }),
      });
      setKillMsg("Kill switch activated.");
      setTimeout(() => setKillMsg(""), 4000);
    } catch {} finally { setKilling(false); }
  };

  const curState = status.state || "ASSISTED";
  const sc = STATE_COLORS[curState] || STATE_COLORS.ASSISTED;
  const last = status.last_decision;
  const ev = last?.unified_context?.ev_sizing;

  return (
    <div className="card">
      {/* Header */}
      <div className="row" style={{ marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>OCI Control Center</div>
          <div style={{ fontSize: 10, color: "var(--muted)" }}>Omega Core Intelligence — unified decision authority</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 5, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{curState}</span>
          <span style={{ fontSize: 9, color: "var(--dim)" }}>History: {status.history_count || 0}</span>
        </div>
      </div>

      {/* State selector */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: "var(--muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>OCI State</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {STATES.map(s => {
            const c = STATE_COLORS[s];
            const active = curState === s;
            return (
              <button key={s} disabled={settingState} onClick={() => setState(s)} style={{
                fontSize: 10, padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontWeight: 600,
                border: active ? `1px solid ${c.border}` : "1px solid var(--border)",
                background: active ? c.bg : "var(--layer2)",
                color: active ? c.color : "var(--muted)",
                opacity: settingState ? 0.6 : 1,
              }}>{s}</button>
            );
          })}
        </div>
        <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 5 }}>
          OBSERVE: read-only · ASSISTED: suggestions only · ARMED: queues orders, needs confirmation · LIVE: full autonomy · DEFENSE: exits only · LOCKED: all activity frozen
        </div>
      </div>

      {/* Last decision */}
      {last && (
        <div style={{ padding: 10, borderRadius: 6, background: "var(--layer1)", border: "1px solid var(--border)", marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: "var(--muted)", marginBottom: 6 }}>Last Decision</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: last.action === "BUY" ? "var(--green)" : last.action === "SELL" ? "var(--red)" : last.action === "BLOCK" ? "#f87171" : "var(--muted)" }}>{last.action}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text)" }}>{last.symbol}</span>
            <span style={{ fontSize: 10, color: "var(--muted)" }}>Confidence: {last.confidence != null ? (last.confidence * 100).toFixed(0) + "%" : "—"}</span>
            <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, background: last.allowed_to_execute ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.1)", color: last.allowed_to_execute ? "#4ade80" : "#f87171" }}>{last.allowed_to_execute ? "EXECUTE ALLOWED" : "HOLD — CONFIRM NEEDED"}</span>
          </div>
          <div style={{ fontSize: 9, color: "var(--muted)", marginBottom: 4 }}>{last.reason}</div>
          {last.hierarchy_path?.length > 0 && (
            <div style={{ fontSize: 8, color: "var(--dim)", display: "flex", gap: 4, flexWrap: "wrap" }}>
              {last.hierarchy_path.map((p, i) => <span key={i} style={{ background: "var(--layer2)", padding: "1px 5px", borderRadius: 3 }}>{p}</span>)}
            </div>
          )}
          {last.warnings?.length > 0 && (
            <div style={{ marginTop: 5, display: "flex", gap: 4, flexWrap: "wrap" }}>
              {last.warnings.map((w, i) => <span key={i} style={{ fontSize: 8, color: "#fbbf24", background: "rgba(251,191,36,0.08)", padding: "1px 5px", borderRadius: 3 }}>{w}</span>)}
            </div>
          )}
          {/* EV sizing from new engine */}
          {ev && (
            <div style={{ marginTop: 8, padding: "6px 8px", borderRadius: 5, background: "rgba(167,139,250,0.06)", borderLeft: "2px solid #a78bfa" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#a78bfa", marginBottom: 3 }}>◆ EV Position Sizing</div>
              <div style={{ display: "flex", gap: 10, fontSize: 9, color: "var(--text)", flexWrap: "wrap" }}>
                <span>Mode: <strong style={{ color: ev.mode === "BLOCK" ? "#f87171" : ev.mode === "AGGRESSIVE" ? "#4ade80" : "#a78bfa" }}>{ev.mode}</strong></span>
                <span>Contracts: <strong>{ev.suggested_contracts}</strong></span>
                <span>Notional: <strong>${(ev.suggested_notional || 0).toFixed(0)}</strong></span>
                <span>Max Risk: <strong>${(ev.max_risk_dollars || 0).toFixed(0)}</strong></span>
                <span>Edge: <strong>{(ev.edge_score || 0).toFixed(1)}</strong></span>
              </div>
              {ev.blocked_reason && <div style={{ fontSize: 8, color: "#f87171", marginTop: 3 }}>Blocked: {ev.blocked_reason}</div>}
            </div>
          )}
        </div>
      )}

      {/* Execution status */}
      {execution && Object.keys(execution).length > 0 && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 10, color: "var(--muted)", marginBottom: 12 }}>
          <span>Mode: <strong style={{ color: "var(--text)" }}>{execution.mode || "—"}</strong></span>
          <span>Orders today: <strong style={{ color: "var(--text)" }}>{execution.orders_today ?? "—"}</strong> / {execution.max_orders_per_day ?? "—"}</span>
          {execution.mode === "KILL_SWITCH" && <span style={{ color: "#f87171", fontWeight: 700 }}>KILL SWITCH ACTIVE</span>}
        </div>
      )}

      {/* Kill switch */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button disabled={killing} onClick={killSwitch} style={{ fontSize: 11, fontWeight: 700, padding: "6px 14px", borderRadius: 6, cursor: "pointer", border: "1px solid rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.1)", color: "#ef4444" }}>
          {killing ? "Activating..." : "Kill Switch"}
        </button>
        {killMsg && <span style={{ fontSize: 10, color: "#f87171" }}>{killMsg}</span>}
      </div>
    </div>
  );
}
